import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'react-feather';
import { Dialog } from '@/shared/components/Dialog';
import { Dropdown } from '@/shared/components/Dropdown';
import { Textarea } from '@/shared/components/TextField';
import { getContactRequestStatusConfig } from '../../hooks/useViewContactRequest';
import type { ContactRequestStatus } from '../../types/contactRequestTypes';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';
import { CONTACT_REQUEST_LABELS as TL } from '../../constants/translations/labels';

const NOTES_MAX_LENGTH = 500;

const InfoIcon: React.FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" stroke="#474A4C" strokeWidth="1.5" />
    <line
      x1="12"
      y1="11"
      x2="12"
      y2="16.5"
      stroke="#474A4C"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="12" cy="8" r="1" fill="#474A4C" />
  </svg>
);

/** Props for the UpdateStatusModal component. */
interface UpdateStatusModalProps {
  open: boolean;
  onClose: () => void;
  currentStatus: ContactRequestStatus;
  allowedStatuses: ContactRequestStatus[];
  newStatus: ContactRequestStatus | '';
  onNewStatusChange: (status: ContactRequestStatus) => void;
  newStatusError: string;
  note: string;
  onNoteChange: (value: string) => void;
  noteError: string;
  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
  canSubmit: boolean;
}

/**
 * Modal dialog for updating the status of a contact request.
 *
 * Renders the current status (read-only), a dropdown for selecting the new
 * status, an optional admin note textarea, and submit/cancel controls.
 * The new-status validation error is intentionally suppressed while the
 * dropdown is open to avoid a jarring flash as the user makes a selection.
 */
const UpdateStatusModal: React.FC<UpdateStatusModalProps> = ({
  open,
  onClose,
  currentStatus,
  allowedStatuses,
  newStatus,
  onNewStatusChange,
  newStatusError,
  note,
  onNoteChange,
  noteError,
  isSubmitting,
  submitError,
  onSubmit,
  canSubmit,
}) => {
  const hasTransitions = allowedStatuses.length > 0;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsDropdownOpen(false);
    }
  }, [open]);

  const displayNewStatusError = isDropdownOpen ? '' : newStatusError;

  const currentCfg = getContactRequestStatusConfig(currentStatus);

  const items = allowedStatuses.map((status) => {
    const cfg = getContactRequestStatusConfig(status);
    return {
      id: status,
      label: '',
      toolTip: false as const,
      leadingIcon: (
        <span
          className="inline-flex items-center gap-2 rounded-lg"
          style={{
            backgroundColor: cfg.bg,
            color: cfg.text,
            paddingLeft: 8,
            paddingRight: 12,
            paddingTop: 6,
            paddingBottom: 6,
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.1px',
            fontWeight: 500,
          }}
        >
          <span
            className="rounded-full shrink-0"
            style={{ width: 6, height: 6, backgroundColor: cfg.text }}
          />
          {cfg.label}
        </span>
      ),
    };
  });

  const selectedCfg = newStatus
    ? getContactRequestStatusConfig(newStatus)
    : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      data-testid="update-cr-status-modal"
    >
      <div
        className="flex flex-col relative"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
      >
        <div className="px-6 pt-6 shrink-0">
          <h2
            className="text-neutral-10 font-normal"
            style={{ fontSize: 22, lineHeight: '28px' }}
            data-testid="update-cr-status-title"
          >
            {TL.UPDATE_STATUS_MODAL_TITLE.polish}
          </h2>
        </div>

        <div className="px-6 pt-4 flex flex-col gap-8 flex-1 min-h-0 overflow-y-auto">
          {/* Current status — read-only */}
          <div
            className="relative cursor-not-allowed max-w-full"
            style={{ height: 56, width: 318 }}
          >
            {/* disabled-state-color overlay */}
            <div className="absolute inset-0 rounded-t bg-[#181A1B] opacity-[.04] pointer-events-none" />
            <div
              className="rounded border-b border-[#181A1B] opacity-[.38] pl-4 py-1 w-full h-full"
              data-testid="update-cr-status-current-field"
              aria-disabled="true"
            >
              <div
                className="flex flex-col justify-center py-1"
                style={{ height: 48 }}
              >
                <span
                  className="pointer-events-none"
                  style={{
                    fontSize: 12,
                    lineHeight: '16px',
                    letterSpacing: '0.4px',
                    color: '#181A1B',
                  }}
                  data-testid="update-cr-status-current-label"
                >
                  {L.UPDATE_STATUS_FIELD_CURRENT}
                </span>
                <span
                  className="inline-flex items-center gap-2 rounded-lg"
                  style={{
                    backgroundColor: currentCfg.bg,
                    color: currentCfg.text,
                    paddingLeft: 8,
                    paddingRight: 10,
                    paddingTop: 4,
                    paddingBottom: 4,
                    fontSize: 14,
                    lineHeight: '20px',
                    letterSpacing: '0.1px',
                    fontWeight: 500,
                    width: 'fit-content',
                  }}
                >
                  <span
                    className="rounded-full shrink-0"
                    style={{
                      width: 6,
                      height: 6,
                      backgroundColor: currentCfg.text,
                    }}
                  />
                  {currentCfg.label}
                </span>
              </div>
            </div>
          </div>

          {/* New status dropdown + irreversibility banner */}
          <div className="flex flex-col gap-2">
            <div
              className="w-[318px] max-w-full"
              data-field="update-cr-new-status"
            >
              <Dropdown
                menuFullWidth
                toolTip={false}
                disabled={!hasTransitions || isSubmitting}
                open={isDropdownOpen}
                onOpenChange={setIsDropdownOpen}
                items={
                  hasTransitions
                    ? items
                    : [
                        {
                          id: '__none__',
                          label: L.UPDATE_STATUS_EMPTY_OPTIONS,
                          disabled: true,
                        },
                      ]
                }
                {...(newStatus ? { selectedId: newStatus } : {})}
                onItemClick={(id) => {
                  if (allowedStatuses.includes(id as ContactRequestStatus)) {
                    onNewStatusChange(id as ContactRequestStatus);
                  }
                }}
                error={Boolean(displayNewStatusError)}
                errorMessage={displayNewStatusError}
                trigger={({ open: isOpen, toggle, disabled, error }) => {
                  const labelFloating = isOpen || !!selectedCfg;
                  const labelColor = isOpen ? '#2a4455' : '#6a7278';
                  return (
                    <button
                      type="button"
                      onClick={disabled ? undefined : toggle}
                      aria-expanded={isOpen}
                      aria-haspopup="true"
                      aria-disabled={disabled}
                      data-testid="update-cr-new-status-button"
                      className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                        disabled
                          ? 'cursor-not-allowed text-neutral-60'
                          : 'cursor-pointer text-neutral-10'
                      }`}
                    >
                      <div
                        className="flex-1 min-w-0 relative flex flex-col justify-center"
                        style={{ minHeight: 40 }}
                      >
                        <span
                          className="absolute left-0 pointer-events-none origin-top-left font-normal"
                          style={{
                            color: labelColor,
                            top: labelFloating ? 0 : 10,
                            fontSize: labelFloating ? 12 : 16,
                            lineHeight: labelFloating ? '16px' : '20px',
                            transition:
                              'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                          }}
                        >
                          {L.UPDATE_STATUS_FIELD_NEW}
                        </span>
                        {selectedCfg && (
                          <span
                            className="inline-flex items-center gap-2 rounded-lg self-start"
                            style={{
                              backgroundColor: selectedCfg.bg,
                              color: selectedCfg.text,
                              paddingLeft: 8,
                              paddingRight: 12,
                              paddingTop: 4,
                              paddingBottom: 4,
                              marginTop: 16,
                              fontSize: 14,
                              lineHeight: '20px',
                              letterSpacing: '0.1px',
                              fontWeight: 500,
                            }}
                          >
                            <span
                              className="rounded-full shrink-0"
                              style={{
                                width: 6,
                                height: 6,
                                backgroundColor: selectedCfg.text,
                              }}
                            />
                            {selectedCfg.label}
                          </span>
                        )}
                      </div>
                      <ChevronDown
                        strokeWidth={1.5}
                        className={`shrink-0 transition-transform duration-200 ${
                          isOpen ? 'rotate-180' : ''
                        } ${error ? 'text-error-40' : ''}`}
                      />
                    </button>
                  );
                }}
              />
            </div>

            <div
              className="flex items-start gap-2"
              data-testid="update-cr-status-irreversible-banner"
            >
              <InfoIcon />
              <p style={{ fontSize: 14, lineHeight: '20px', color: '#474A4C' }}>
                <span className="font-semibold">
                  {L.UPDATE_STATUS_BANNER_BOLD}
                </span>
                {L.UPDATE_STATUS_BANNER_REST}
              </p>
            </div>
          </div>

          {/* Optional admin note — hidden when the user is about to cancel */}
          {newStatus !== 'cancelled' && (
            <div data-field="update-cr-note">
              <Textarea
                label={
                  <>
                    {L.UPDATE_STATUS_FIELD_NOTE}{' '}
                    <span style={{ fontSize: 12 }}>
                      {L.UPDATE_STATUS_FIELD_NOTE_OPTIONAL}
                    </span>
                  </>
                }
                placeholder={L.UPDATE_STATUS_PLACEHOLDER_NOTE}
                value={note}
                onChange={(e) =>
                  onNoteChange((e.target as HTMLTextAreaElement).value ?? '')
                }
                maxLength={NOTES_MAX_LENGTH}
                rows={4}
                fullWidth
                error={Boolean(noteError)}
                errorMessage={noteError}
                disabled={isSubmitting}
                dataTestId="update-cr-note-input"
              />
            </div>
          )}

          {submitError && (
            <p
              className="text-error-40"
              style={{ fontSize: 12, lineHeight: '16px' }}
              data-testid="update-cr-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-6 shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid="update-cr-status-cancel"
          >
            {L.UPDATE_STATUS_BUTTON_CANCEL}
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              canSubmit
                ? 'hover:bg-[#1f3340] cursor-pointer'
                : 'opacity-50 cursor-not-allowed'
            }`}
            data-testid="update-cr-status-submit"
          >
            {isSubmitting
              ? L.UPDATE_STATUS_BUTTON_SUBMITTING
              : newStatus === 'cancelled'
                ? TL.UPDATE_STATUS_BUTTON_CONTINUE.polish
                : L.UPDATE_STATUS_BUTTON_SUBMIT}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default UpdateStatusModal;
